import { zodResolver } from "@hookform/resolvers/zod";
import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  LockKeyIcon,
  ShieldCheckIcon,
} from "@phosphor-icons/react";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useToast } from "@/client/hooks/use-toast";
import { useUpdatePassword } from "@/client/services/auth";
import { useUser } from "@/client/services/user";

const passwordSchema = z
  .object({
    currentPassword: z
      .string()
      .min(6, "Le mot de passe actuel doit contenir au moins 6 caractères."),
    newPassword: z.string().min(8, "Le nouveau mot de passe doit contenir au moins 8 caractères."),
    confirmation: z.string().min(1, "Confirmez votre nouveau mot de passe."),
  })
  .refine((values) => values.newPassword === values.confirmation, {
    path: ["confirmation"],
    message: "Les deux mots de passe ne correspondent pas.",
  });

type PasswordFormValues = z.infer<typeof passwordSchema>;

type PasswordInputProps = {
  value: string;
  onChange: (...event: unknown[]) => void;
  onBlur: () => void;
  name: string;
  placeholder: string;
  autoComplete: string;
};

const PasswordInput = ({
  value,
  onChange,
  onBlur,
  name,
  placeholder,
  autoComplete,
}: PasswordInputProps) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <LockKeyIcon
        aria-hidden
        size={17}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
      />

      <Input
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        type={visible ? "text" : "password"}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className="h-11 rounded-xl pl-10 pr-11 text-sm"
      />

      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        className="absolute right-2 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
      >
        {visible ? <EyeSlashIcon aria-hidden size={17} /> : <EyeIcon aria-hidden size={17} />}
      </button>
    </div>
  );
};

export const UserSecuritySettings = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const { updatePassword, loading } = useUpdatePassword();

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmation: "",
    },
  });

  const handleSubmit = async (values: PasswordFormValues) => {
    await updatePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });

    form.reset();

    toast({
      variant: "success",
      title: "Mot de passe modifié",
      description: "Votre nouveau mot de passe est maintenant actif.",
    });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
      <section
        className={cn(
          "rounded-2xl border border-slate-200 bg-white",
          "p-4 shadow-[0_8px_30px_rgba(15,23,42,0.05)]",
          "sm:p-6 lg:p-7",
        )}
      >
        <div className="flex items-start gap-3 border-b border-slate-200 pb-5">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
            <KeyIcon aria-hidden size={21} weight="duotone" />
          </span>

          <div>
            <h2 className="text-base font-bold text-indigo-950">Modifier le mot de passe</h2>

            <p className="mt-1 text-xs leading-5 text-slate-500">
              Choisissez un mot de passe unique que vous n’utilisez pas sur un autre service.
            </p>
          </div>
        </div>

        <Form {...form}>
          <form className="mt-5 space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              name="currentPassword"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-semibold text-indigo-950">
                    Mot de passe actuel
                  </FormLabel>

                  <FormControl>
                    <PasswordInput
                      {...field}
                      autoComplete="current-password"
                      placeholder="Saisissez votre mot de passe actuel"
                    />
                  </FormControl>

                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <div className="grid gap-5 md:grid-cols-2">
              <FormField
                name="newPassword"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-indigo-950">
                      Nouveau mot de passe
                    </FormLabel>

                    <FormControl>
                      <PasswordInput
                        {...field}
                        autoComplete="new-password"
                        placeholder="8 caractères minimum"
                      />
                    </FormControl>

                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              <FormField
                name="confirmation"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-indigo-950">
                      Confirmation
                    </FormLabel>

                    <FormControl>
                      <PasswordInput
                        {...field}
                        autoComplete="new-password"
                        placeholder="Confirmez le mot de passe"
                      />
                    </FormControl>

                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                disabled={!form.formState.isDirty || loading}
                onClick={() => form.reset()}
                className="rounded-xl text-xs"
              >
                Annuler
              </Button>

              <Button
                type="submit"
                disabled={!form.formState.isDirty || loading}
                className="rounded-xl bg-indigo-600 text-xs hover:bg-indigo-700"
              >
                <ShieldCheckIcon aria-hidden className="mr-2" size={16} />

                {loading ? "Modification…" : "Modifier le mot de passe"}
              </Button>
            </div>
          </form>
        </Form>
      </section>

      <aside className="space-y-4">
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5">
          <div className="flex items-start gap-3">
            <CheckCircleIcon
              aria-hidden
              size={22}
              weight="fill"
              className="shrink-0 text-emerald-600"
            />

            <div>
              <h3 className="text-sm font-bold text-emerald-950">Compte protégé</h3>

              <p className="mt-1 text-xs leading-5 text-emerald-800/80">
                Votre session et vos informations personnelles sont protégées.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-bold text-indigo-950">Connexion actuelle</h3>

          <p className="mt-2 text-xs leading-5 text-slate-500">Adresse : {user?.email}</p>

          <p className="mt-1 text-xs leading-5 text-slate-500">Fournisseur : {user?.provider}</p>
        </section>
      </aside>
    </div>
  );
};
